import React, { useEffect, useRef, useState, useContext } from 'react';
import './styles.css';

import P from 'prop-types';
import { Button } from '../Button';
import API from '../../utils/api/office.instance';
import { toast } from 'react-toastify';
import { GlobalContext } from '../../contexts/HomeContext';
import { SaveUser } from '../../utils/utils';
import { SolicitarCodigoSmsPorTelefone } from '../../utils/api/api';
import { OtpInput } from '../OtpInput';

const RESEND_COOLDOWN_SECONDS = 30;

// Normaliza os campos de telefone dos diferentes formatos de `cliente`
// (Login/MeusDados usam `telefone_ddd`, PreCadastro usa `ddd`).
const normalizePhone = (cliente) => {
  const ddd = String(cliente?.telefone_ddd || cliente?.ddd || '').replace(/\D/g, '');
  const telefone = String(cliente?.telefone || '').replace(/\D/g, '');
  return { ddd, telefone };
};

export const ConfirmaSms = ({ modalParam }) => {
  const [codigo, setCodigo] = useState('');
  const [validating, setValidating] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const { dadosApp, setUser } = useContext(GlobalContext);

  const isOtpMode = modalParam?.modo === 'qlub-telefone';
  const codigoLength = Number(modalParam?.length) > 0 ? Number(modalParam.length) : 6;
  const onlyDigits = String(codigo).replace(/\D/g, '');
  const isComplete = onlyDigits.length === codigoLength;
  const lastAutoSubmittedRef = useRef('');

  const { ddd: clienteDdd, telefone: clienteTelefone } = normalizePhone(modalParam?.cliente);
  const canResend = clienteDdd.length >= 2 && clienteTelefone.length >= 8;
  // O reenvio padrão usa o endpoint por telefone, que só encontra cliente em gw_mobile_clientes.
  // Fluxos com outra base de cadastro (ex.: Portaria/PreCadastro) desabilitam com podeReenviar: false.
  const showResend = modalParam?.podeReenviar !== false && (Boolean(modalParam?.onReenviar) || canResend);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const doSubmit = async (digits) => {
    if (validating) return;
    setValidating(true);
    try {
      // caso seja pelo PreCadastro ou pelo fluxo Qlub telefone-duplicado
      if (modalParam.onClick) {
        await modalParam.onClick(digits);
        return;
      }

      const obj = {
        codigo: digits,
        cliente: modalParam.cliente,
      };

      // //Chamar api de Autenticação
      await API.post('/office/AutoAtendimento/Autenticar', obj)
        .then((response) => {
          if (response.data == 0) {
            toast.error('Usuário não encontrado no cadastro!', { containerId: 'modalToast' });
          } else {
            modalParam.cliente.ativo = 1; // IMPORTANTE
            SaveUser(modalParam.cliente.idloja, modalParam.cliente, setUser);

            //encryptStorage.setItem(modalParam.idloja + 'user', JSON.stringify(modalParam));
            toast.success('Bem vindo ' + modalParam.cliente.nome + '!', { containerId: 'toastGeral' });
            if (modalParam.closeCadastro) setTimeout(() => modalParam.closeCadastro(), 600);
          }
        })
        .catch((err) => {
          toast.error('Erro interno do Servidor! ' + err, { containerId: 'modalToast' });
        });
    } finally {
      setValidating(false);
    }
    // //Gravar dados usuario na localstorage
  };

  // Envio automático ao completar todos os dígitos (padrão das grandes empresas).
  // Guarda o último código auto-enviado para não reenviar em loop após erro de validação.
  useEffect(() => {
    if (!isComplete || validating) return;
    if (lastAutoSubmittedRef.current === onlyDigits) return;
    lastAutoSubmittedRef.current = onlyDigits;
    doSubmit(onlyDigits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete, onlyDigits]);

  const handleAutenticar = async (event) => {
    event?.preventDefault(); // <-- Isso evita o refresh da página
    if (isOtpMode && onlyDigits.length !== codigoLength) {
      toast.warn(`Informe o código de ${codigoLength} dígitos enviado por SMS`, { containerId: 'modalToast' });
      return;
    }
    if (!isOtpMode && onlyDigits.length === 0) {
      toast.warn('Informe o código enviado por SMS', { containerId: 'modalToast' });
      return;
    }
    lastAutoSubmittedRef.current = onlyDigits;
    await doSubmit(onlyDigits);
  };

  // Reenvio padrão: reaproveita o endpoint por telefone. O backend decide o canal
  // (SMS + email, ou só SMS quando o email for fake `pedido_*`).
  const handleDefaultReenviar = async () => {
    const idloja = modalParam?.cliente?.idloja || dadosApp?.IdLoja;
    const respSms = await SolicitarCodigoSmsPorTelefone({
      idloja,
      telefone_ddd: clienteDdd,
      telefone: clienteTelefone,
    });
    const failed = respSms?.data?.status === 1 || respSms?.data?.Retorno?.status === 1;
    if (failed) {
      const msg =
        respSms?.data?.msg || respSms?.data?.Retorno?.msg || 'Não foi possível reenviar o código. Tente novamente.';
      throw new Error(typeof msg === 'string' ? msg : 'Não foi possível reenviar o código. Tente novamente.');
    }
    // Backend sinaliza que o reenvio foi bloqueado pelo throttle (60s) e que o código
    // anterior continua válido. Nesse caso não anunciamos "código reenviado".
    if (respSms?.data?.reenvioBloqueado === true) {
      toast.info(
        respSms?.data?.msg ||
          'Já enviamos um código por SMS para este telefone. Se não o recebeu, aguarde 1 minuto para pedir um novo.',
        {
          containerId: 'modalToast',
          autoClose: 5000,
        },
      );
      return;
    }
    toast.info('Código reenviado.', { containerId: 'modalToast', autoClose: 5000 });
  };

  const handleReenviarClick = async () => {
    try {
      if (modalParam?.onReenviar) await modalParam.onReenviar();
      else await handleDefaultReenviar();
    } catch (error) {
      console.error('Erro ao reenviar código SMS', error);
      toast.error(error?.message || 'Não foi possível reenviar o código. Tente novamente.', {
        containerId: 'modalToast',
      });
    }
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const resendBlock = showResend ? (
    <div style={{ marginTop: 16, marginBottom: 24, paddingBottom: 24, textAlign: 'center' }}>
      <span style={{ fontSize: 15 }}>
        Não recebeu?{' '}
        <button
          type="button"
          className="otp-resend"
          style={{ color: 'rgb(54, 146, 233)' }}
          disabled={cooldown > 0 || validating}
          onClick={handleReenviarClick}
        >
          {cooldown > 0 ? `Reenviar em ${cooldown}s` : 'Reenviar código'}
        </button>
      </span>
    </div>
  ) : null;

  return (
    <div className={'modal-content-dialog'}>
      {isOtpMode ? (
        <form onSubmit={handleAutenticar}>
          <div className={'form-group'} style={{ marginBottom: 20 }}>
            <p style={{ marginBottom: 4, textAlign: 'center' }}>
              {modalParam.subtitle || `Enviamos um código de ${codigoLength} dígitos por SMS para`}
            </p>
            <p style={{ marginBottom: 8, textAlign: 'center', fontWeight: 600 }}>
              {'(' + modalParam.cliente.telefone_ddd + ') ' + modalParam.cliente.telefone}
            </p>
            <p style={{ marginBottom: 8, textAlign: 'center' }}>para confirmar que é você.</p>
            <OtpInput length={codigoLength} value={codigo} onChange={setCodigo} disabled={validating} />
          </div>
          <Button
            text={validating ? 'Confirmando...' : 'Confirmar'}
            className="btnGradient btn-width-full"
            type="submit"
          />
          {resendBlock}
        </form>
      ) : (
        <>
          <form onSubmit={handleAutenticar}>
            <div className={'form-group'} style={{ marginBottom: 20 }}>
              <p style={{ marginBottom: 20, textAlign: 'center' }}>Código enviado por SMS e por Email:</p>
              <p style={{ textAlign: 'center' }}>
                {/* Conclua seu cadastro inserindo o código enviado para:{' '} */}
                {'(' + modalParam.cliente.telefone_ddd + ') ' + modalParam.cliente.telefone}
              </p>
              <p style={{ marginBottom: 8, textAlign: 'center' }}>
                {/* Conclua seu cadastro inserindo o código enviado para:{' '} */}
                {modalParam.cliente.email}
              </p>

              <p style={{ textAlign: 'center', marginBottom: 10, fontWeight: 'bold' }}>Informe o Código</p>
              <OtpInput length={codigoLength} value={codigo} onChange={setCodigo} disabled={validating} />
            </div>
            <Button
              text={validating ? 'Autenticando...' : 'Autenticar'}
              className="btnGradient btn-width-full"
              type="submit"
            />
            {resendBlock}
          </form>
          <div className={'clearfix-checkbox'}></div>
        </>
      )}
    </div>
  );
};

ConfirmaSms.propTypes = {
  modalParam: P.object,
};
